import type { LeadId } from "./crm";

export type ConversationId = string & {
  readonly __brand: "ConversationId";
};
export type ConversationMessageId = string & {
  readonly __brand: "ConversationMessageId";
};
export type PublicChatSessionId = string & {
  readonly __brand: "PublicChatSessionId";
};
export type PublicChatClientMessageId = string & {
  readonly __brand: "PublicChatClientMessageId";
};
export type ConversationCursor = string & {
  readonly __brand: "ConversationCursor";
};
export type ConversationHandoffEventId = string & {
  readonly __brand: "ConversationHandoffEventId";
};
export type CommunicationConsentEventId = string & {
  readonly __brand: "CommunicationConsentEventId";
};
export type ChatAiOutputMetadataId = string & {
  readonly __brand: "ChatAiOutputMetadataId";
};

export const publicChatBoundaryVersion = "2026-08-05" as const;
export const publicChatSafetyCopy = Object.freeze({
  nonMedical:
    "This chat can help with services, locations, hours, accessibility, and booking. Do not share symptoms, diagnoses, medications, treatment details, or other medical information.",
  emergency:
    "This chat is not an emergency service. If you may be in immediate danger or have a medical emergency, call 911 or your local emergency service now.",
  structuredOnly:
    "Choose one of the listed topics. Unrestricted messages are not accepted in public chat.",
  clinicalRejected:
    "Medical or clinical details cannot be collected here. Remove those details and choose a general service, location, hours, accessibility, booking, contact, or handoff option.",
  handoff:
    "A human follow-up was requested. Do not add medical or emergency details.",
} as const);

export const publicChatFaqKeys = [
  "SERVICES",
  "LOCATIONS",
  "HOURS",
  "BOOKING",
  "ACCESSIBILITY",
] as const;
export type PublicChatFaqKey = (typeof publicChatFaqKeys)[number];

export const publicChatContactChannels = ["EMAIL", "PHONE"] as const;
export type PublicChatContactChannel =
  (typeof publicChatContactChannels)[number];

export const publicChatHandoffReasons = [
  "GENERAL_SUPPORT",
  "ACCESSIBILITY_SUPPORT",
  "OTHER_NON_MEDICAL",
] as const;
export type PublicChatHandoffReason = (typeof publicChatHandoffReasons)[number];

export const publicChatIntentKinds = ["FAQ", "CONTACT", "HANDOFF"] as const;
export type PublicChatIntentKind = (typeof publicChatIntentKinds)[number];

export interface PublicChatFaqContent {
  readonly kind: "FAQ";
  readonly faqKey: PublicChatFaqKey;
}

export interface PublicChatContactContent {
  readonly kind: "CONTACT";
  readonly channel: PublicChatContactChannel;
}

export interface PublicChatHandoffContent {
  readonly kind: "HANDOFF";
  readonly reason: PublicChatHandoffReason;
}

export type PublicChatStructuredContent =
  | PublicChatFaqContent
  | PublicChatContactContent
  | PublicChatHandoffContent;

export interface PublicChatSessionCreateRequest {
  readonly boundaryVersion: typeof publicChatBoundaryVersion;
  readonly boundaryAcknowledged: true;
}

export interface PublicChatSessionCreated {
  readonly sessionId: PublicChatSessionId;
  readonly conversationId: ConversationId;
  readonly expiresAt: string;
  readonly boundaryVersion: typeof publicChatBoundaryVersion;
}

export interface PublicChatMessageRequest {
  readonly clientMessageId: PublicChatClientMessageId;
  readonly content: PublicChatStructuredContent;
}

export interface PublicChatMessageAccepted {
  readonly messageId: ConversationMessageId;
  readonly conversationId: ConversationId;
  readonly intent: PublicChatIntentKind;
  readonly handoffState: ConversationHandoffState;
  readonly acceptedAt: string;
}

export function isPublicChatSessionCreated(
  value: unknown,
): value is PublicChatSessionCreated {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "sessionId",
      "conversationId",
      "expiresAt",
      "boundaryVersion",
    ]) &&
    isSafeIdentifier(value.sessionId) &&
    isSafeIdentifier(value.conversationId) &&
    isCanonicalChatTimestamp(value.expiresAt) &&
    value.boundaryVersion === publicChatBoundaryVersion
  );
}

export function isPublicChatMessageAccepted(
  value: unknown,
): value is PublicChatMessageAccepted {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "messageId",
      "conversationId",
      "intent",
      "handoffState",
      "acceptedAt",
    ]) &&
    isSafeIdentifier(value.messageId) &&
    isSafeIdentifier(value.conversationId) &&
    isEnumValue(value.intent, publicChatIntentKinds) &&
    isEnumValue(value.handoffState, conversationHandoffStates) &&
    isCanonicalChatTimestamp(value.acceptedAt)
  );
}

export const conversationStatuses = [
  "OPEN",
  "WAITING_FOR_STAFF",
  "IN_PROGRESS",
  "CLOSED",
  "SPAM",
] as const;
export type ConversationStatus = (typeof conversationStatuses)[number];

export const conversationHandoffStates = [
  "NONE",
  "REQUESTED",
  "ACKNOWLEDGED",
  "RESOLVED",
] as const;
export type ConversationHandoffState =
  (typeof conversationHandoffStates)[number];

export const adminConversationPageSizes = [25] as const;
export type AdminConversationPageSize =
  (typeof adminConversationPageSizes)[number];

export interface AdminConversationInboxItem {
  readonly conversationId: ConversationId;
  readonly leadId: LeadId | null;
  readonly status: ConversationStatus;
  readonly statusRevision: number;
  readonly assignedUserId: string | null;
  readonly assignmentRevision: number;
  readonly handoffState: ConversationHandoffState;
  readonly handoffRevision: number;
  readonly messageCount: number;
  readonly lastMessageAt: string | null;
  readonly updatedAt: string;
}

export interface AdminConversationInboxQuery {
  readonly cursor: ConversationCursor | null;
  readonly limit: AdminConversationPageSize;
}

export interface AdminConversationInboxPage {
  readonly items: readonly AdminConversationInboxItem[];
  readonly nextCursor: ConversationCursor | null;
}

export interface AdminConversationAssignmentRequest {
  readonly conversationId: ConversationId;
  readonly expectedAssigneeUserId: string | null;
  readonly targetAssigneeUserId: string | null;
  readonly expectedAssignmentRevision: number;
  readonly expectedUpdatedAt: string;
}

export interface AdminConversationAssignmentSnapshot {
  readonly conversationId: ConversationId;
  readonly assignedUserId: string | null;
  readonly assignmentRevision: number;
  readonly updatedAt: string;
}

export interface AdminConversationStatusRequest {
  readonly conversationId: ConversationId;
  readonly expectedStatus: ConversationStatus;
  readonly targetStatus: ConversationStatus;
  readonly expectedStatusRevision: number;
  readonly expectedUpdatedAt: string;
}

export interface AdminConversationStatusSnapshot {
  readonly conversationId: ConversationId;
  readonly status: ConversationStatus;
  readonly statusRevision: number;
  readonly updatedAt: string;
}

export interface AdminConversationHandoffRequest {
  readonly conversationId: ConversationId;
  readonly expectedState: ConversationHandoffState;
  readonly targetState: ConversationHandoffState;
  readonly expectedHandoffRevision: number;
  readonly expectedUpdatedAt: string;
}

export interface AdminConversationHandoffSnapshot {
  readonly conversationId: ConversationId;
  readonly handoffState: ConversationHandoffState;
  readonly handoffRevision: number;
  readonly updatedAt: string;
}

export const communicationConsentChannels = [
  "ALL",
  "EMAIL",
  "SMS",
  "PHONE",
  "PUBLIC_CHAT",
] as const;
export type CommunicationConsentChannel =
  (typeof communicationConsentChannels)[number];

export const communicationConsentDecisions = ["OPT_IN", "OPT_OUT"] as const;
export type CommunicationConsentDecision =
  (typeof communicationConsentDecisions)[number];

export interface CommunicationConsentChangeRequest {
  readonly channel: CommunicationConsentChannel;
  readonly decision: CommunicationConsentDecision;
  readonly policyVersion: string;
  readonly expectedRevision: number;
}

export interface CommunicationConsentSnapshot {
  readonly eventId: CommunicationConsentEventId;
  readonly channel: CommunicationConsentChannel;
  readonly decision: CommunicationConsentDecision;
  readonly policyVersion: string;
  readonly revision: number;
  readonly occurredAt: string;
}

export const communicationPurposes = [
  "DIRECT_RESPONSE",
  "OPERATIONAL",
  "MARKETING",
] as const;
export type CommunicationPurpose = (typeof communicationPurposes)[number];

export const chatAiOutputOutcomes = [
  "SERVED",
  "BLOCKED",
  "HANDOFF",
  "FAILED",
] as const;
export type ChatAiOutputOutcome = (typeof chatAiOutputOutcomes)[number];

export interface ChatAiOutputMetadata {
  readonly eventId: ChatAiOutputMetadataId;
  readonly conversationId: ConversationId;
  readonly faqKey: PublicChatFaqKey | null;
  readonly providerKey: string;
  readonly modelKey: string;
  readonly policyVersion: string;
  readonly knowledgeScopeVersion: string;
  readonly outcome: ChatAiOutputOutcome;
  readonly latencyMs: number | null;
  readonly inputTokenCount: number | null;
  readonly outputTokenCount: number | null;
  readonly correlationId: string;
  readonly createdAt: string;
}

const safeIdentifierPattern = /^[A-Za-z0-9_-]+$/;
const safeVersionPattern = /^[A-Za-z0-9._-]+$/;

function isObject(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactOwnKeys(
  value: object,
  expectedKeys: readonly string[],
): boolean {
  const actualKeys = Reflect.ownKeys(value);
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every(
      (key) => typeof key === "string" && expectedKeys.includes(key),
    )
  );
}

function isEnumValue<T extends string>(
  value: unknown,
  values: readonly T[],
): value is T {
  return typeof value === "string" && values.includes(value as T);
}

function isSafeIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 128 &&
    safeIdentifierPattern.test(value)
  );
}

function isSafeVersion(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 128 &&
    safeVersionPattern.test(value)
  );
}

function isNonNegativeRevision(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 2_147_483_647
  );
}

function isOptionalCount(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === "number" &&
      Number.isSafeInteger(value) &&
      value >= 0 &&
      value <= 2_147_483_647)
  );
}

function isNullableIdentifier(value: unknown): value is string | null {
  return value === null || isSafeIdentifier(value);
}

export function isCanonicalChatTimestamp(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }
  const milliseconds = Date.parse(value);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === value
  );
}

export function isPublicChatSessionCreateRequest(
  value: unknown,
): value is PublicChatSessionCreateRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, ["boundaryVersion", "boundaryAcknowledged"]) &&
    value.boundaryVersion === publicChatBoundaryVersion &&
    value.boundaryAcknowledged === true
  );
}

export function isPublicChatStructuredContent(
  value: unknown,
): value is PublicChatStructuredContent {
  if (!isObject(value) || typeof value.kind !== "string") return false;
  switch (value.kind) {
    case "FAQ":
      return (
        hasExactOwnKeys(value, ["kind", "faqKey"]) &&
        isEnumValue(value.faqKey, publicChatFaqKeys)
      );
    case "CONTACT":
      return (
        hasExactOwnKeys(value, ["kind", "channel"]) &&
        isEnumValue(value.channel, publicChatContactChannels)
      );
    case "HANDOFF":
      return (
        hasExactOwnKeys(value, ["kind", "reason"]) &&
        isEnumValue(value.reason, publicChatHandoffReasons)
      );
    default:
      return false;
  }
}

export function isPublicChatMessageRequest(
  value: unknown,
): value is PublicChatMessageRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, ["clientMessageId", "content"]) &&
    isSafeIdentifier(value.clientMessageId) &&
    isPublicChatStructuredContent(value.content)
  );
}

export function isAdminConversationInboxItem(
  value: unknown,
): value is AdminConversationInboxItem {
  if (
    !isObject(value) ||
    !hasExactOwnKeys(value, [
      "conversationId",
      "leadId",
      "status",
      "statusRevision",
      "assignedUserId",
      "assignmentRevision",
      "handoffState",
      "handoffRevision",
      "messageCount",
      "lastMessageAt",
      "updatedAt",
    ])
  ) {
    return false;
  }
  return (
    isSafeIdentifier(value.conversationId) &&
    isNullableIdentifier(value.leadId) &&
    isEnumValue(value.status, conversationStatuses) &&
    isNonNegativeRevision(value.statusRevision) &&
    isNullableIdentifier(value.assignedUserId) &&
    isNonNegativeRevision(value.assignmentRevision) &&
    isEnumValue(value.handoffState, conversationHandoffStates) &&
    isNonNegativeRevision(value.handoffRevision) &&
    isNonNegativeRevision(value.messageCount) &&
    (value.lastMessageAt === null ||
      isCanonicalChatTimestamp(value.lastMessageAt)) &&
    isCanonicalChatTimestamp(value.updatedAt) &&
    ((value.messageCount === 0 && value.lastMessageAt === null) ||
      (value.messageCount > 0 &&
        isCanonicalChatTimestamp(value.lastMessageAt) &&
        value.lastMessageAt <= value.updatedAt))
  );
}

export function isAdminConversationInboxPage(
  value: unknown,
): value is AdminConversationInboxPage {
  if (
    !isObject(value) ||
    !hasExactOwnKeys(value, ["items", "nextCursor"]) ||
    !Array.isArray(value.items) ||
    value.items.length > adminConversationPageSizes[0] ||
    (value.nextCursor !== null &&
      !(
        typeof value.nextCursor === "string" &&
        value.nextCursor.length >= 8 &&
        value.nextCursor.length <= 512 &&
        safeIdentifierPattern.test(value.nextCursor)
      ))
  ) {
    return false;
  }
  const items = value.items;
  const seen = new Set<string>();
  for (const item of items) {
    if (!isAdminConversationInboxItem(item) || seen.has(item.conversationId)) {
      return false;
    }
    seen.add(item.conversationId);
  }
  return items.every((item, index) => {
    const previous = items[index - 1];
    return (
      previous === undefined ||
      previous.updatedAt > item.updatedAt ||
      (previous.updatedAt === item.updatedAt &&
        previous.conversationId < item.conversationId)
    );
  });
}

export function isAdminConversationAssignmentRequest(
  value: unknown,
): value is AdminConversationAssignmentRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "conversationId",
      "expectedAssigneeUserId",
      "targetAssigneeUserId",
      "expectedAssignmentRevision",
      "expectedUpdatedAt",
    ]) &&
    isSafeIdentifier(value.conversationId) &&
    isNullableIdentifier(value.expectedAssigneeUserId) &&
    isNullableIdentifier(value.targetAssigneeUserId) &&
    value.expectedAssigneeUserId !== value.targetAssigneeUserId &&
    isNonNegativeRevision(value.expectedAssignmentRevision) &&
    isCanonicalChatTimestamp(value.expectedUpdatedAt)
  );
}

export function isAdminConversationStatusRequest(
  value: unknown,
): value is AdminConversationStatusRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "conversationId",
      "expectedStatus",
      "targetStatus",
      "expectedStatusRevision",
      "expectedUpdatedAt",
    ]) &&
    isSafeIdentifier(value.conversationId) &&
    isEnumValue(value.expectedStatus, conversationStatuses) &&
    isEnumValue(value.targetStatus, conversationStatuses) &&
    value.expectedStatus !== value.targetStatus &&
    isNonNegativeRevision(value.expectedStatusRevision) &&
    isCanonicalChatTimestamp(value.expectedUpdatedAt)
  );
}

export function isAdminConversationHandoffRequest(
  value: unknown,
): value is AdminConversationHandoffRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "conversationId",
      "expectedState",
      "targetState",
      "expectedHandoffRevision",
      "expectedUpdatedAt",
    ]) &&
    isSafeIdentifier(value.conversationId) &&
    isEnumValue(value.expectedState, conversationHandoffStates) &&
    isEnumValue(value.targetState, conversationHandoffStates) &&
    value.expectedState !== value.targetState &&
    isNonNegativeRevision(value.expectedHandoffRevision) &&
    isCanonicalChatTimestamp(value.expectedUpdatedAt)
  );
}

export function isCommunicationConsentChangeRequest(
  value: unknown,
): value is CommunicationConsentChangeRequest {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "channel",
      "decision",
      "policyVersion",
      "expectedRevision",
    ]) &&
    isEnumValue(value.channel, communicationConsentChannels) &&
    isEnumValue(value.decision, communicationConsentDecisions) &&
    isSafeVersion(value.policyVersion) &&
    isNonNegativeRevision(value.expectedRevision)
  );
}

export function isCommunicationConsentSnapshot(
  value: unknown,
): value is CommunicationConsentSnapshot {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "eventId",
      "channel",
      "decision",
      "policyVersion",
      "revision",
      "occurredAt",
    ]) &&
    isSafeIdentifier(value.eventId) &&
    isEnumValue(value.channel, communicationConsentChannels) &&
    isEnumValue(value.decision, communicationConsentDecisions) &&
    isSafeVersion(value.policyVersion) &&
    isNonNegativeRevision(value.revision) &&
    value.revision > 0 &&
    isCanonicalChatTimestamp(value.occurredAt)
  );
}

export function isChatAiOutputMetadata(
  value: unknown,
): value is ChatAiOutputMetadata {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, [
      "eventId",
      "conversationId",
      "faqKey",
      "providerKey",
      "modelKey",
      "policyVersion",
      "knowledgeScopeVersion",
      "outcome",
      "latencyMs",
      "inputTokenCount",
      "outputTokenCount",
      "correlationId",
      "createdAt",
    ]) &&
    isSafeIdentifier(value.eventId) &&
    isSafeIdentifier(value.conversationId) &&
    (value.faqKey === null || isEnumValue(value.faqKey, publicChatFaqKeys)) &&
    isSafeVersion(value.providerKey) &&
    isSafeVersion(value.modelKey) &&
    isSafeVersion(value.policyVersion) &&
    isSafeVersion(value.knowledgeScopeVersion) &&
    isEnumValue(value.outcome, chatAiOutputOutcomes) &&
    isOptionalCount(value.latencyMs) &&
    isOptionalCount(value.inputTokenCount) &&
    isOptionalCount(value.outputTokenCount) &&
    isSafeIdentifier(value.correlationId) &&
    isCanonicalChatTimestamp(value.createdAt)
  );
}
